import { useMemo } from 'react';
import { useStore } from 'store/useStore';
import { ExternalItemLink, PlanBoardItem, StoryNode } from 'types';

function toPlanBoardItem(
    node: StoryNode,
    treeId: string | null,
    treeName: string,
    link?: ExternalItemLink,
): PlanBoardItem {
    return {
        id: node.id,
        type: node.type === 'experiment' ? 'experiment' : node.type === 'enablerNode' ? 'enabler' : 'story',
        title: node.title,
        description: node.description || '',
        treeId,
        treeName,
        priority: node.moscow,
        link,
    };
}

/**
 * Derives every story/experiment/enabler node across all of the active project's trees,
 * for display as cards in the Plan page's Plan column (see PlanColumn.tsx).
 *
 * The currently active tree's nodes are read live from the store (so in-progress
 * edits show up immediately); every other tree's nodes come from the cached
 * snapshot on activeProject.osts, mirroring the same osts/live-store split
 * InsightsPage's `trees` memo uses.
 *
 * Each item also carries its GitHub issue link, if the Plan page's Sync has made one. That map
 * is project-level and server-owned (activeProject.externalLinks), which is what lets a card
 * belonging to a NON-active tree still show its issue — the node itself is only a cached
 * snapshot there, and its githubUrl may not have been written.
 */
export default function usePlanBoardItems(): PlanBoardItem[] {
    const activeProject = useStore((s) => s.activeProject);
    const userStories = useStore((s) => s.userStories);
    const activeOstId = useStore((s) => s.activeOstId);

    return useMemo(() => {
        const osts = activeProject?.osts;
        const links = activeProject?.externalLinks?.items || {};

        if (osts && osts.length > 0) {
            return osts.flatMap((o) => {
                const treeId = String(o._id);
                const treeName = o.name || 'Unnamed Tree';
                const nodes: StoryNode[] = treeId === activeOstId ? userStories : (o.ost?.userStories || []);
                return nodes.map((n) => toPlanBoardItem(n, treeId, treeName, links[n.id]));
            });
        }

        // Legacy single-tree project: no osts array, just the live userStories.
        const treeName = activeProject?.name || 'Main Tree';
        return userStories.map((n) => toPlanBoardItem(n, null, treeName, links[n.id]));
    }, [activeProject, userStories, activeOstId]);
}
